import {
  appendArrayItem,
  blankFromTemplate,
  contentMatchesSearch,
  humanizeKey,
  isBrandKey,
  isLocalized,
  isPlainObject,
  moveArrayItem,
  pathLabel,
  removeArrayItem,
  updateAtPath
} from "../lib/content";
import type { JsonPath, JsonValue } from "../types";

interface EditorProps {
  root: JsonValue;
  path?: JsonPath;
  value?: JsonValue;
  label?: string;
  depth?: number;
  search: string;
  onChange: (next: JsonValue) => void;
}

function fieldId(path: JsonPath, language?: string): string {
  const base =
    path
      .map((part) => String(part))
      .join("-")
      .replace(/[^a-z0-9_-]/gi, "-") || "root";
  return `field-${base}${language ? `-${language}` : ""}`;
}

function shouldUseTextarea(path: JsonPath, value: string): boolean {
  const key = String(path[path.length - 1] || "").toLowerCase();
  return (
    value.length > 80 ||
    value.includes("\n") ||
    ["description", "intro", "body", "address", "statement", "notes"].some(
      (part) => key.includes(part)
    )
  );
}

function LocalizedField({
  root,
  value,
  path,
  label,
  onChange
}: {
  root: JsonValue;
  value: { en: string; tr: string };
  path: JsonPath;
  label: string;
  onChange: (next: JsonValue) => void;
}) {
  const locked = isBrandKey(path[path.length - 1]);

  function changeLanguage(language: "en" | "tr", text: string) {
    onChange(updateAtPath(root, [...path, language], text));
  }

  return (
    <fieldset className="localized-field">
      <legend>
        {label}
        {locked && <span className="locked-label">Locked brand</span>}
      </legend>
      <p className="field-path">{pathLabel(path)}</p>
      <div className="localized-grid">
        {(["en", "tr"] as const).map((language) => (
          <div className="field" key={language}>
            <label htmlFor={fieldId(path, language)}>
              {language === "en" ? "English" : "Türkçe"}
            </label>
            {shouldUseTextarea(path, value[language]) ? (
              <textarea
                id={fieldId(path, language)}
                rows={3}
                value={locked ? "ATALAY" : value[language]}
                readOnly={locked}
                onChange={(event) => changeLanguage(language, event.target.value)}
              />
            ) : (
              <input
                id={fieldId(path, language)}
                value={locked ? "ATALAY" : value[language]}
                readOnly={locked}
                onChange={(event) => changeLanguage(language, event.target.value)}
              />
            )}
          </div>
        ))}
      </div>
    </fieldset>
  );
}

function PrimitiveField({
  root,
  value,
  path,
  label,
  onChange
}: {
  root: JsonValue;
  value: string | number | boolean | null;
  path: JsonPath;
  label: string;
  onChange: (next: JsonValue) => void;
}) {
  const locked = isBrandKey(path[path.length - 1]);
  const id = fieldId(path);
  const update = (next: JsonValue) => onChange(updateAtPath(root, path, next));

  if (typeof value === "boolean") {
    return (
      <div className="field field--toggle">
        <label htmlFor={id}>
          <span>{label}</span>
          <small>{pathLabel(path)}</small>
        </label>
        <input
          id={id}
          type="checkbox"
          checked={value}
          onChange={(event) => update(event.target.checked)}
        />
      </div>
    );
  }

  if (typeof value === "number") {
    return (
      <div className="field">
        <label htmlFor={id}>{label}</label>
        <p className="field-path">{pathLabel(path)}</p>
        <input
          id={id}
          type="number"
          value={value}
          onChange={(event) =>
            update(event.target.value === "" ? 0 : Number(event.target.value))
          }
        />
      </div>
    );
  }

  if (value === null) {
    return (
      <div className="null-field">
        <span>
          <strong>{label}</strong>
          <small>{pathLabel(path)} · No value</small>
        </span>
        <button type="button" className="button button--small" onClick={() => update("")}>
          Set text value
        </button>
      </div>
    );
  }

  const shownValue = locked ? "ATALAY" : value;
  return (
    <div className="field">
      <label htmlFor={id}>
        {label}
        {locked && <span className="locked-label">Locked brand</span>}
      </label>
      <p className="field-path">{pathLabel(path)}</p>
      {shouldUseTextarea(path, value) ? (
        <textarea
          id={id}
          rows={4}
          value={shownValue}
          readOnly={locked}
          onChange={(event) => update(event.target.value)}
        />
      ) : (
        <input
          id={id}
          value={shownValue}
          readOnly={locked}
          onChange={(event) => update(event.target.value)}
        />
      )}
    </div>
  );
}

function ArrayEditor({
  root,
  value,
  path,
  label,
  depth,
  search,
  onChange
}: {
  root: JsonValue;
  value: JsonValue[];
  path: JsonPath;
  label: string;
  depth: number;
  search: string;
  onChange: (next: JsonValue) => void;
}) {
  const shownItems = value
    .map((item, index) => ({ item, index }))
    .filter(({ item, index }) =>
      contentMatchesSearch(item, [...path, index], search)
    );

  function addFromSchema() {
    const item = value.length > 0 ? blankFromTemplate(value[0]) : { en: "", tr: "" };
    onChange(appendArrayItem(root, path, item));
  }

  function remove(index: number) {
    const itemName = `item ${index + 1} from ${label}`;
    if (!window.confirm(`Remove ${itemName}? This can be undone until the draft is saved.`)) {
      return;
    }
    onChange(removeArrayItem(root, path, index));
  }

  return (
    <section className="array-editor">
      <div className="array-editor__heading">
        <div>
          <h3>{label}</h3>
          <p>
            {value.length} {value.length === 1 ? "item" : "items"} · {pathLabel(path)}
          </p>
        </div>
        <button type="button" className="button button--small" onClick={addFromSchema}>
          Add item
        </button>
      </div>
      {shownItems.length === 0 ? (
        <div className="array-editor__empty">
          <p>{search ? "No array items match this search." : "This list is empty."}</p>
          {!search && value.length === 0 && (
            <div className="inline-actions">
              <button
                type="button"
                className="text-button"
                onClick={() =>
                  onChange(appendArrayItem(root, path, { en: "", tr: "" }))
                }
              >
                Add bilingual text
              </button>
              <button
                type="button"
                className="text-button"
                onClick={() => onChange(appendArrayItem(root, path, ""))}
              >
                Add plain text
              </button>
              <button
                type="button"
                className="text-button"
                onClick={() => onChange(appendArrayItem(root, path, {}))}
              >
                Add object
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="array-list">
          {shownItems.map(({ item, index }) => (
            <article className="array-item" key={`${pathLabel(path)}-${index}`}>
              <div className="array-item__toolbar">
                <strong>Item {index + 1}</strong>
                <div>
                  <button
                    type="button"
                    className="icon-button"
                    onClick={() => onChange(moveArrayItem(root, path, index, index - 1))}
                    disabled={index === 0}
                    aria-label={`Move item ${index + 1} up`}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="icon-button"
                    onClick={() => onChange(moveArrayItem(root, path, index, index + 1))}
                    disabled={index === value.length - 1}
                    aria-label={`Move item ${index + 1} down`}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="icon-button icon-button--danger"
                    onClick={() => remove(index)}
                    aria-label={`Remove item ${index + 1}`}
                  >
                    ×
                  </button>
                </div>
              </div>
              <RecursiveEditor
                root={root}
                value={item}
                path={[...path, index]}
                label={`Item ${index + 1}`}
                depth={depth + 1}
                search={search}
                onChange={onChange}
              />
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

export function RecursiveEditor({
  root,
  value = root,
  path = [],
  label = "Content",
  depth = 0,
  search,
  onChange
}: EditorProps) {
  if (!contentMatchesSearch(value, path, search)) return null;

  if (isLocalized(value)) {
    return (
      <LocalizedField
        root={root}
        value={value}
        path={path}
        label={label}
        onChange={onChange}
      />
    );
  }

  if (Array.isArray(value)) {
    return (
      <ArrayEditor
        root={root}
        value={value}
        path={path}
        label={label}
        depth={depth}
        search={search}
        onChange={onChange}
      />
    );
  }

  if (isPlainObject(value)) {
    const entries = Object.entries(value).filter(([key, child]) =>
      contentMatchesSearch(child, [...path, key], search)
    );
    if (depth === 0) {
      return (
        <div className="root-editor">
          {entries.map(([key, child]) => (
            <RecursiveEditor
              key={key}
              root={root}
              value={child}
              path={[...path, key]}
              label={humanizeKey(key)}
              depth={depth + 1}
              search={search}
              onChange={onChange}
            />
          ))}
        </div>
      );
    }

    return (
      <details className="object-editor" open={Boolean(search) || depth < 2}>
        <summary>
          <span>{label}</span>
          <small>
            {entries.length} {entries.length === 1 ? "field" : "fields"}
          </small>
        </summary>
        <div className="object-editor__body">
          {entries.map(([key, child]) => (
            <RecursiveEditor
              key={key}
              root={root}
              value={child}
              path={[...path, key]}
              label={humanizeKey(key)}
              depth={depth + 1}
              search={search}
              onChange={onChange}
            />
          ))}
        </div>
      </details>
    );
  }

  return (
    <PrimitiveField
      root={root}
      value={value}
      path={path}
      label={label}
      onChange={onChange}
    />
  );
}
